import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useCallback, lazy, Suspense } from 'react';
import { LayoutGrid, List, X, Package, PlusCircle, ArrowUpDown, Share2, RotateCcw } from 'lucide-react';
import { CONDITIONS, VIDEO_SYSTEMS } from '@siliconvault/shared';
import { Shell } from '../../components/layout/Shell';
import { ItemCard } from '../../components/inventory/ItemCard';
import { ItemTable } from '../../components/inventory/ItemTable';
import { SkeletonItemCard } from '../../components/ui/Skeleton';
import { Input, Select } from '../../components/ui/Input';
import { ComboBox } from '../../components/ui/ComboBox';
import { Button } from '../../components/ui/Button';
import { useInventoryList } from '../../hooks/useInventory';
import { useExport } from '../../hooks/useExport';
import { usePreferences } from '../../hooks/usePreferences';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../i18n';
import { useAppMode } from '../../context/AppModeContext';
import { useVault } from '../../context/VaultContext';
import { usePlatformLabel } from '../../hooks/usePlatformLabel';
import { useDebounce } from '../../hooks/useDebounce';
import { useTaxonomySuggestions } from '../../hooks/useTaxonomy';
import { Route as rootRoute } from '../__root';
import type { PdfLayout, PdfOrientation } from '../../lib/pdfCatalog';
import styles from './index.module.css';

const PdfExportModal = lazy(() => import('../../components/inventory/PdfExportModal').then(m => ({ default: m.PdfExportModal })));
const ShareCatalogModal = lazy(() => import('../../components/community/ShareCatalogModal').then(m => ({ default: m.ShareCatalogModal })));

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory',
  component: InventoryListPage,
});

function getPageNumbers(current: number, total: number): Array<number | '...'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
  if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
}

const INVENTORY_FILTERS_KEY = 'sv_inventory_filters';

interface SavedFilters {
  search?: string;
  category?: string;
  platform?: string;
  subcategory?: string;
  condition?: string;
  videoSystem?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  view?: 'grid' | 'table';
  showAll?: boolean;
}

function loadSavedFilters(): SavedFilters {
  try {
    const raw = localStorage.getItem(INVENTORY_FILTERS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveFilters(filters: SavedFilters) {
  try {
    localStorage.setItem(INVENTORY_FILTERS_KEY, JSON.stringify(filters));
  } catch { /* ignore quota errors */ }
}

function InventoryListPage() {
  const { preferences } = usePreferences();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { mode, communityEnabled, aiTier } = useAppMode();
  const { vaultTypeDef, activeVaultId } = useVault();
  const platformLabel = usePlatformLabel();
  const showVideoSystem = vaultTypeDef
    ? vaultTypeDef.specializedFeatures.video_system === true
    : true; // no vault type def = legacy mode, show it

  const [saved] = useState(loadSavedFilters);
  const [view, setView] = useState<'grid' | 'table'>(saved.view ?? preferences.defaultView);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(saved.search ?? '');
  const [category, setCategory] = useState(saved.category ?? '');
  const [platform, setPlatform] = useState(saved.platform ?? '');
  const [subcategory, setSubcategory] = useState(saved.subcategory ?? '');
  const [condition, setCondition] = useState(saved.condition ?? '');
  const [videoSystem, setVideoSystem] = useState(saved.videoSystem ?? '');
  const [sortBy, setSortBy] = useState(saved.sortBy ?? 'createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(saved.sortOrder ?? 'desc');
  const [showAll, setShowAll] = useState(saved.showAll ?? false);

  // Persist filters to localStorage whenever they change
  const persistFilters = useCallback((overrides: Partial<SavedFilters> = {}) => {
    // We read current state via the overrides pattern — callers pass the new value
    const current = loadSavedFilters();
    saveFilters({ ...current, ...overrides });
  }, []);

  const debouncedSearch = useDebounce(search, 300);
  const { exportCsv, exportJson, exportPdf, pdfGenerating, pdfProgress } = useExport();
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const isPro = mode === 'desktop' || aiTier === 'pro';

  const { suggestions: categorySuggestions } = useTaxonomySuggestions('category');
  const { suggestions: platformSuggestions } = useTaxonomySuggestions('platform');
  const { suggestions: subcategorySuggestions } = useTaxonomySuggestions('subcategory', category || undefined, platform || undefined);

  const { data, isLoading } = useInventoryList({
    page,
    limit: preferences.itemsPerPage,
    search: debouncedSearch || undefined,
    category: category || undefined,
    platform: platform || undefined,
    condition: (condition || undefined) as typeof CONDITIONS[number] | undefined,
    videoSystem: (videoSystem || undefined) as typeof VIDEO_SYSTEMS[number] | undefined,
    subcategory: subcategory || undefined,
    sortBy: sortBy as any,
    sortOrder,
    showAll: isAdmin ? showAll : undefined,
  });

  function handleSort(column: string) {
    if (sortBy === column) {
      const next = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(next);
      persistFilters({ sortOrder: next });
    } else {
      setSortBy(column);
      setSortOrder('asc');
      persistFilters({ sortBy: column, sortOrder: 'asc' });
    }
  }

  const exportFilters: Record<string, string> = {};
  if (activeVaultId) exportFilters.vaultId = activeVaultId;
  if (debouncedSearch) exportFilters.search = debouncedSearch;
  if (category) exportFilters.category = category;
  if (platform) exportFilters.platform = platform;
  if (subcategory) exportFilters.subcategory = subcategory;
  if (condition) exportFilters.condition = condition;
  if (videoSystem) exportFilters.videoSystem = videoSystem;
  if (isAdmin && showAll) exportFilters.showAll = 'true';

  // Active filter chips
  const activeFilters: Array<{ key: string; label: string; clear: () => void }> = [];
  if (category) activeFilters.push({ key: 'category', label: category, clear: () => { setCategory(''); setPage(1); persistFilters({ category: '' }); } });
  if (platform) activeFilters.push({ key: 'platform', label: platform, clear: () => { setPlatform(''); setPage(1); persistFilters({ platform: '' }); } });
  if (subcategory) activeFilters.push({ key: 'subcategory', label: subcategory, clear: () => { setSubcategory(''); setPage(1); persistFilters({ subcategory: '' }); } });
  if (condition) activeFilters.push({ key: 'condition', label: t(`condition.${condition}` as any), clear: () => { setCondition(''); setPage(1); persistFilters({ condition: '' }); } });
  if (videoSystem) activeFilters.push({ key: 'videoSystem', label: t(`videoSystem.${videoSystem}` as any), clear: () => { setVideoSystem(''); setPage(1); persistFilters({ videoSystem: '' }); } });
  if (isAdmin && showAll) activeFilters.push({ key: 'showAll', label: t('inventory.allUsers'), clear: () => { setShowAll(false); setPage(1); persistFilters({ showAll: false }); } });

  const hasNonDefaultFilters = search || category || platform || subcategory || condition || videoSystem || sortBy !== 'createdAt' || sortOrder !== 'desc' || (isAdmin && showAll);

  function clearAll() {
    setSearch(''); setCategory(''); setPlatform(''); setSubcategory(''); setCondition(''); setVideoSystem('');
    setSortBy('createdAt'); setSortOrder('desc'); setShowAll(false); setPage(1);
    localStorage.removeItem(INVENTORY_FILTERS_KEY);
  }

  const pageNumbers = data ? getPageNumbers(page, data.totalPages) : [];

  return (
    <Shell>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('inventory.title')}</h1>
          <div className={styles.headerActions}>
            <Button variant="secondary" size="small" onClick={() => exportCsv(exportFilters)}>
              {t('inventory.exportCsv')}
            </Button>
            <Button variant="secondary" size="small" onClick={() => exportJson(exportFilters)}>
              {t('inventory.exportJson')}
            </Button>
            <Button variant="secondary" size="small" onClick={() => isPro ? setShowPdfModal(true) : navigate({ to: '/upgrade' })}>
              {t('inventory.exportPdf')}
            </Button>
            {communityEnabled && (
              <Button variant="secondary" size="small" onClick={() => setShowShareModal(true)}>
                <Share2 size={13} strokeWidth={2} /> {t('share.inventoryShareBtn')}
              </Button>
            )}
            <Link to="/inventory/new">
              <Button size="small"><PlusCircle size={13} strokeWidth={2} /> {t('inventory.addItem')}</Button>
            </Link>
          </div>
        </div>

        {/* Filter bar */}
        <div className={styles.filters}>
          {/* Zone 1 — Search */}
          <div className={styles.searchZone}>
            <Input
              placeholder={t('inventory.searchPlaceholder')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); persistFilters({ search: e.target.value }); }}
            />
          </div>

          {/* Zone 2 — Filter dropdowns */}
          <div className={styles.filterZone}>
            <div className={styles.filterDropdown}>
              <ComboBox
                value={category}
                onChange={(v) => { setCategory(v); setPage(1); persistFilters({ category: v }); }}
                suggestions={categorySuggestions}
                placeholder={t('inventory.allCategories')}
              />
            </div>
            <div className={styles.filterDropdown}>
              <ComboBox
                value={platform}
                onChange={(v) => { setPlatform(v); setPage(1); persistFilters({ platform: v }); }}
                suggestions={platformSuggestions}
                placeholder={`All ${platformLabel}s`}
              />
            </div>
            <div className={styles.filterDropdown}>
              <ComboBox
                value={subcategory}
                onChange={(v) => { setSubcategory(v); setPage(1); persistFilters({ subcategory: v }); }}
                suggestions={subcategorySuggestions}
                placeholder={t('inventory.allSubcategories')}
              />
            </div>
            <div className={styles.filterDropdown}>
              <Select value={condition} onChange={(e) => { setCondition(e.target.value); setPage(1); persistFilters({ condition: e.target.value }); }}>
                <option value="">{t('inventory.allConditions')}</option>
                {CONDITIONS.map((c) => <option key={c} value={c}>{t(`condition.${c}` as any)}</option>)}
              </Select>
            </div>
            {showVideoSystem && (
              <div className={styles.filterDropdown}>
                <Select value={videoSystem} onChange={(e) => { setVideoSystem(e.target.value); setPage(1); persistFilters({ videoSystem: e.target.value }); }}>
                  <option value="">{t('inventory.allVideoSystems')}</option>
                  {VIDEO_SYSTEMS.map((vs) => <option key={vs} value={vs}>{t(`videoSystem.${vs}` as any)}</option>)}
                </Select>
              </div>
            )}
          </div>

          {/* Zone 3 — Controls */}
          <div className={styles.controlZone}>
            <div className={styles.sortUnit}>
              <Select
                className={styles.sortSelect}
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setPage(1); persistFilters({ sortBy: e.target.value }); }}
              >
                <option value="createdAt">{t('inventory.sort.createdAt')}</option>
                <option value="updatedAt">{t('inventory.sort.updatedAt')}</option>
                <option value="name">{t('inventory.sort.name')}</option>
                <option value="category">{t('inventory.sort.category')}</option>
                <option value="platform">{platformLabel}</option>
                <option value="condition">{t('inventory.sort.condition')}</option>
                <option value="subcategory">{t('inventory.sort.subcategory')}</option>
                <option value="acquisitionDate">{t('inventory.sort.acquisitionDate')}</option>
                <option value="acquisitionPrice">{t('inventory.sort.acquisitionPrice')}</option>
                <option value="launchDate">{t('inventory.sort.launchDate')}</option>
                <option value="launchPrice">{t('inventory.sort.launchPrice')}</option>
              </Select>
              <button
                className={styles.sortDirBtn}
                onClick={() => { const next = sortOrder === 'asc' ? 'desc' : 'asc'; setSortOrder(next); persistFilters({ sortOrder: next }); }}
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                <ArrowUpDown size={14} strokeWidth={2} />
              </button>
            </div>
            {isAdmin && (
              <Button
                size="small"
                variant={showAll ? 'primary' : 'secondary'}
                onClick={() => { const next = !showAll; setShowAll(next); setPage(1); persistFilters({ showAll: next }); }}
              >
                {showAll ? t('inventory.allUsers') : t('inventory.myItems')}
              </Button>
            )}
            <Button variant="ghost" size="small" onClick={clearAll} disabled={!hasNonDefaultFilters}>
              <RotateCcw size={13} strokeWidth={2} />
              {t('inventory.resetFilters')}
            </Button>
            <div className={styles.viewToggle}>
              <button
                className={`${styles.viewBtn} ${view === 'grid' ? styles.viewBtnActive : ''}`}
                onClick={() => { setView('grid'); persistFilters({ view: 'grid' }); }}
                title={t('inventory.gridView')}
              >
                <LayoutGrid size={15} strokeWidth={1.75} />
              </button>
              <button
                className={`${styles.viewBtn} ${view === 'table' ? styles.viewBtnActive : ''}`}
                onClick={() => { setView('table'); persistFilters({ view: 'table' }); }}
                title={t('inventory.tableView')}
              >
                <List size={15} strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </div>

        {/* Active filter chips */}
        {activeFilters.length > 0 && (
          <div className={styles.activeFilters}>
            {activeFilters.map((f) => (
              <span key={f.key} className={styles.filterChip}>
                {f.label}
                <button className={styles.filterChipRemove} onClick={f.clear} aria-label={`Remove ${f.label} filter`}>
                  <X size={11} strokeWidth={2.5} />
                </button>
              </span>
            ))}
            {activeFilters.length >= 2 && (
              <button className={styles.clearAllLink} onClick={clearAll}>{t('inventory.clearAll')}</button>
            )}
            {data && (
              <span className={styles.resultCount}>
                {data.total} {data.total !== 1 ? t('common.results') : t('common.result')}
              </span>
            )}
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          view === 'grid' ? (
            <div className={styles.grid}>
              {Array.from({ length: preferences.itemsPerPage > 12 ? 12 : preferences.itemsPerPage }).map((_, i) => (
                <SkeletonItemCard key={i} />
              ))}
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              {t('inventory.loadingInventory')}
            </div>
          )
        ) : view === 'grid' ? (
          <div className={styles.grid}>
            {data?.data.map((item) => (
              <ItemCard key={item.id} item={item} showOwner={showAll} />
            ))}
            {data?.data.length === 0 && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon}><Package size={56} strokeWidth={1} /></span>
                  <div className={styles.emptyTitle}>
                    {activeFilters.length > 0 ? t('inventory.noMatch') : t('inventory.emptyVault')}
                  </div>
                  <div className={styles.emptyDesc}>
                    {activeFilters.length > 0
                      ? t('inventory.noMatchDesc')
                      : vaultTypeDef
                        ? `Add your first ${vaultTypeDef.label.toLowerCase()} item to get started.`
                        : t('inventory.emptyDesc')}
                  </div>
                  {activeFilters.length > 0 ? (
                    <button className={styles.clearAllLink} onClick={clearAll} style={{ fontSize: '0.8125rem' }}>
                      {t('inventory.clearFilters')}
                    </button>
                  ) : (
                    <Link to="/inventory/new">
                      <Button size="small"><PlusCircle size={13} strokeWidth={2} /> Add Item</Button>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <ItemTable
            items={data?.data ?? []}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            showOwner={showAll}
          />
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageBtn}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ←
            </button>
            {pageNumbers.map((n, i) =>
              n === '...' ? (
                <span key={`ellipsis-${i}`} className={styles.pageEllipsis}>…</span>
              ) : (
                <button
                  key={n}
                  className={`${styles.pageBtn} ${n === page ? styles.pageBtnActive : ''}`}
                  onClick={() => setPage(n as number)}
                >
                  {n}
                </button>
              )
            )}
            <button
              className={styles.pageBtn}
              disabled={page >= data.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              →
            </button>
          </div>
        )}
      </div>

      <Suspense>
        {showShareModal && (
          <ShareCatalogModal onClose={() => setShowShareModal(false)} />
        )}
        {showPdfModal && (
          <PdfExportModal
            isOpen={showPdfModal}
            onClose={() => setShowPdfModal(false)}
            onGenerate={(layout: PdfLayout, orientation: PdfOrientation) => {
              const filterLabels = activeFilters.map((f) => f.label);
              exportPdf(exportFilters, layout, filterLabels.length > 0 ? filterLabels : undefined, orientation).then(() => setShowPdfModal(false));
            }}
            isGenerating={pdfGenerating}
            progress={pdfProgress}
          />
        )}
      </Suspense>
    </Shell>
  );
}
